/**
 * <newsletter-popup>
 * Floating (bottom-left, non-modal) email-capture card backed by the native
 * Shopify customer form, with a persistent teaser chip.
 *
 * State machine (localStorage):
 * - subscribed: nothing ever shows again
 * - teaser dismissed via its X: nothing for data-dismiss-days
 * - popup closed without subscribing: teaser chip shows on this and future
 *   pages; clicking it reopens the form
 * - fresh visitor: popup auto-opens once after data-delay seconds
 * Post-back renders (success or validation errors) open immediately.
 * Never renders or triggers on Shopify's /challenge (captcha) page.
 * Optional birthday selects are folded into the customer tags on submit
 * (birthday:MM-DD or birthday:MM-DD-YYYY).
 */
if (!customElements.get('newsletter-popup')) {
  const SUBSCRIBED_KEY = 'lh-newsletter-subscribed';
  const DISMISSED_KEY = 'lh-newsletter-dismissed-at';
  const MINIMIZED_KEY = 'lh-newsletter-minimized';

  const store = {
    get(key) { try { return localStorage.getItem(key); } catch (e) { return null; } },
    set(key, value) { try { localStorage.setItem(key, value); } catch (e) { /* private mode */ } },
    remove(key) { try { localStorage.removeItem(key); } catch (e) { /* private mode */ } },
  };

  class NewsletterPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.teaser = this.querySelector('[data-popup-teaser]');
      if (!this.dialog) return;
      if (location.pathname.startsWith('/challenge')) return;
      const state = this.querySelector('[data-popup-state]')?.dataset.popupState;

      this.onKeydown = (event) => {
        if (event.key === 'Escape' && this.dialog.open) this.minimize();
      };
      document.addEventListener('keydown', this.onKeydown);

      this.querySelectorAll('[data-popup-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.minimize())
      );
      this.querySelector('[data-popup-teaser-open]')?.addEventListener('click', () => {
        this.hideTeaser();
        this.open();
        this.querySelector('#lh-popup-first-name, #lh-popup-email')?.focus();
      });
      this.querySelector('[data-popup-teaser-close]')?.addEventListener('click', () => {
        this.hideTeaser();
        store.remove(MINIMIZED_KEY);
        store.set(DISMISSED_KEY, String(Date.now()));
      });
      this.querySelector('form')?.addEventListener('submit', () => {
        this.applyBirthdayTag();
        this.track('newsletter_popup_submit');
        // If Shopify's invisible captcha escalates to a visible challenge,
        // hide the card so the challenge is unobstructed; the post-back
        // render reopens with the outcome.
        setTimeout(() => {
          if (this.dialog.open) this.dialog.close();
        }, 1200);
      });
      this.querySelector('[data-popup-copy]')?.addEventListener('click', (event) => {
        const btn = event.currentTarget;
        navigator.clipboard?.writeText(btn.dataset.code).then(() => {
          btn.textContent = 'Copied';
          setTimeout(() => { btn.textContent = 'Copy code'; }, 2000);
        });
      });

      if (state === 'success') {
        store.set(SUBSCRIBED_KEY, '1');
        store.remove(MINIMIZED_KEY);
        this.undoAnchorJump();
        this.open();
      } else if (state === 'error') {
        this.undoAnchorJump();
        this.open();
      } else if (this.subscribedOrDismissed()) {
        // stay hidden
      } else if (store.get(MINIMIZED_KEY)) {
        this.showTeaser();
      } else {
        const delay = (parseInt(this.dataset.delay, 10) || 8) * 1000;
        this.timer = setTimeout(() => this.open(), delay);
      }
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('keydown', this.onKeydown);
    }

    subscribedOrDismissed() {
      if (window.Shopify && window.Shopify.designMode) return true;
      if (store.get(SUBSCRIBED_KEY)) return true;
      const dismissedAt = parseInt(store.get(DISMISSED_KEY) || '0', 10);
      const dismissDays = parseInt(this.dataset.dismissDays, 10) || 30;
      return !!dismissedAt && Date.now() - dismissedAt < dismissDays * 86400000;
    }

    // The customer form's default redirect targets /#contact_form, which
    // scrolls to the footer newsletter anchor. return_to avoids it, but if
    // the fragment slips through, undo the jump.
    undoAnchorJump() {
      if (location.hash === '#contact_form') {
        history.replaceState(null, '', location.pathname + location.search);
        window.scrollTo({ top: 0, behavior: 'instant' });
      }
    }

    applyBirthdayTag() {
      const get = (part) => this.querySelector(`[data-popup-bday="${part}"]`)?.value || '';
      const month = get('month');
      const day = get('day');
      const year = get('year');
      if (!month || !day) return;
      const tagsInput = this.querySelector('[data-popup-tags]');
      if (!tagsInput) return;
      let tag = `birthday:${month}-${day}`;
      if (year) tag += `-${year}`;
      tagsInput.value = `${tagsInput.value},${tag}`;
    }

    open() {
      if (this.dialog.open) return;
      this.hideTeaser();
      // Set the open attribute directly: shows the non-modal dialog without
      // running the focusing steps, so the shopper's focus is never stolen
      // on auto-open. (Teaser clicks focus the first field explicitly.)
      this.dialog.setAttribute('open', '');
      this.track('newsletter_popup_view');
    }

    // Closing the form without subscribing collapses it to the teaser chip.
    minimize() {
      this.dialog.close();
      store.set(MINIMIZED_KEY, '1');
      this.showTeaser();
    }

    showTeaser() {
      if (this.teaser) {
        this.teaser.hidden = false;
        this.track('newsletter_teaser_view');
      }
    }

    hideTeaser() {
      if (this.teaser) this.teaser.hidden = true;
    }

    track(event) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event });
    }
  }

  customElements.define('newsletter-popup', NewsletterPopup);
}
